import React, { useCallback, useEffect, useState } from "react";
import "./index.css";
import { Button, DatePicker, Select, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import GenericTable from "../GeneralTable/GenericTable";
import { useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AuditLogItem, getAuditLogs } from "./auditTrails.service";
import { Content } from "antd/es/layout/layout";
import HeaderOfList from "../../assets/img/auditTrailsHeader.png";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const AuditTrails: React.FC = () => {
  const { t } = useTranslation("auditTrails");
  const { id: projectId } = useParams<{ id: string }>();

  const FILTER_OPTIONS = [
    { value: "created", label: t("filterOptions.created") },
    { value: "edited", label: t("filterOptions.edited") },
    { value: "deleted", label: t("filterOptions.deleted") },
    { value: "viewed", label: t("filterOptions.viewed") },
    { value: "executed", label: t("filterOptions.executed") },
    { value: "reverted", label: t("filterOptions.reverted") },
  ];

  const [filter, setFilter] = useState<string | null>(null);
  const [dateCreated, setDateCreated] = useState<Dayjs | null>(null);
  const [dateModified, setDateModified] = useState<Dayjs | null>(null);

  const [data, setData] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getAuditLogs({
        project_id: projectId,
        filter: filter ?? undefined,
        date_created: dateCreated
          ? dateCreated.format("YYYY-MM-DD")
          : undefined,
        date_modified: dateModified
          ? dateModified.format("YYYY-MM-DD")
          : undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setData(res.items);
      setTotal(res.total);
    } catch {
      message.error(t("messages.fetchError"));
    } finally {
      setLoading(false);
    }
  }, [projectId, filter, dateCreated, dateModified, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const resetPage = () => setPage(1);

  const columns: ColumnsType<AuditLogItem> = [
    {
      title: t("columns.user"),
      key: "user",
      render: (_: any, row: AuditLogItem) => (
        <span className="audit-cell-user">{row.user_full_name ?? "—"}</span>
      ),
    },
    {
      title: t("columns.dateCreated"),
      key: "created_at",
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (_: any, row: AuditLogItem) => (
        <span className="audit-cell-date">
          {dayjs(row.created_at).format("DD/MM/YYYY")}
        </span>
      ),
    },
    {
      title: t("columns.dateModified"),
      key: "modified_at",
      render: (_: any, row: AuditLogItem) => (
        <span className="audit-cell-date">
          {row.modified_at ? dayjs(row.modified_at).format("DD/MM/YYYY") : "—"}
        </span>
      ),
    },
    {
      title: t("columns.actionType"),
      key: "action",
      render: (_: any, row: AuditLogItem) => (
        <span className="audit-cell-action">{row.action}</span>
      ),
    },
  ];

  const handleExport = async () => {
    if (!projectId) return;
    try {
      const res = await getAuditLogs({
        project_id: projectId,
        filter: filter ?? undefined,
        date_created: dateCreated
          ? dateCreated.format("YYYY-MM-DD")
          : undefined,
        date_modified: dateModified
          ? dateModified.format("YYYY-MM-DD")
          : undefined,
        page: 1,
        page_size: 500,
      });

      const header = [
        t("columns.user"),
        t("columns.dateCreated"),
        t("columns.dateModified"),
        t("columns.actionType"),
      ];
      const rows = res.items.map((item) => [
        item.user_full_name ?? "",
        dayjs(item.created_at).format("DD/MM/YYYY"),
        item.modified_at ? dayjs(item.modified_at).format("DD/MM/YYYY") : "",
        item.action,
      ]);

      const csv = [header, ...rows]
        .map((r) =>
          r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-trails.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error(t("messages.exportError"));
    }
  };

  return (
    <div style={{ marginLeft: 0 }} className="dashboard-ai-layout">
      <Content className="card audit-container">
        <div className="header-image-for-dashboard">
          <img style={{ width: "100%" }} src={HeaderOfList} alt="header" />
        </div>
        {/* <div className="card audit-container"> */}
        <div className="audit-header">
          <div>
            <h2>{t("title")}</h2>
            <p>{t("description")}</p>
          </div>
          <Button className="audit-exportbtn" onClick={handleExport}>
            <DownloadOutlined className="audit-exportIcon" />
            {t("exportButton")}
          </Button>
        </div>

        <div className="audit-filters-card">
          <div className="audit-filter-group">
            <div className="audit-filter-label">{t("filterLabel")}</div>
            <Select
              className="audit-select"
              placeholder={t("filterPlaceholder")}
              value={filter}
              onChange={(v) => {
                setFilter(v);
                resetPage();
              }}
              allowClear
              options={FILTER_OPTIONS}
            />
          </div>

          <div className="audit-filter-group">
            <div className="audit-filter-label">{t("columns.dateCreated")}</div>
            <DatePicker
              className="audit-date"
              placeholder={t("datePlaceholder")}
              value={dateCreated}
              onChange={(v) => {
                setDateCreated(v);
                resetPage();
              }}
            />
          </div>

          <div className="audit-filter-group">
            <div className="audit-filter-label">{t("columns.dateModified")}</div>
            <DatePicker
              className="audit-date"
              placeholder={t("datePlaceholder")}
              value={dateModified}
              onChange={(v) => {
                setDateModified(v);
                resetPage();
              }}
            />
          </div>
        </div>

        <div className="audit-table-card">
          <GenericTable<AuditLogItem>
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              onChange: (p) => setPage(p),
            }}
            emptyText={t("emptyText")}
            style={{ width: "100%" }}
          />
        </div>
      </Content>
    </div>
  );
};

export default AuditTrails;
